export type ServiceConflictsByName = Record<string, string[]>;

type ConflictPattern = {
  regex: RegExp;
  namesFromMatch: (match: RegExpMatchArray) => [string[], string[]];
};

const splitNames = (raw: string): string[] => raw.split(',').map((name) => name.trim());

// Backend message shapes that report an incompatible-service conflict (can arrive alone or
// comma-joined with unrelated messages, so neither pattern is anchored to the whole string).
// Both parseServiceConflicts and isServiceConflictMessage read from this one list so they
// can't drift out of sync when a new shape is added.
const CONFLICT_PATTERNS: ConflictPattern[] = [
  {
    // Services::CompatibilityChecker: "Service(s) [A, B] are incompatible with service(s) [C, D]"
    regex: /Service\(s\) \[(.+?)\] are incompatible with service\(s\) \[(.+?)\]/gi,
    namesFromMatch: (match) => [splitNames(match[1]), splitNames(match[2])],
  },
  {
    // ServiceRequirement#ambulatory_wav_mutual_exclusion / #ride_recording_mutual_exclusion:
    // "A cannot be assigned to a rider that has B service assigned." Excludes commas from the
    // name groups so this can't cross into an unrelated message joined right before it.
    regex: /([^,]+?) cannot be assigned to a rider that has ([^,]+?) service assigned\./gi,
    namesFromMatch: (match) => [[match[1].trim()], [match[2].trim()]],
  },
];

const addConflict = (conflicts: ServiceConflictsByName, name: string, conflictingNames: string[]) => {
  const existing = conflicts[name] ?? [];

  conflicts[name] = [...new Set([...existing, ...conflictingNames])];
};

export const parseServiceConflicts = (messages: string[]): ServiceConflictsByName => {
  const conflicts: ServiceConflictsByName = {};

  messages.forEach((message) => {
    CONFLICT_PATTERNS.forEach(({ regex, namesFromMatch }) => {
      for (const match of message.matchAll(regex)) {
        const [groupA, groupB] = namesFromMatch(match);

        groupA.forEach((name) => addConflict(conflicts, name, groupB));
        groupB.forEach((name) => addConflict(conflicts, name, groupA));
      }
    });
  });

  return conflicts;
};

// A fresh RegExp per call avoids lastIndex statefulness on the shared global-flagged patterns.
export const isServiceConflictMessage = (message: string): boolean =>
  CONFLICT_PATTERNS.some(({ regex }) => new RegExp(regex.source, regex.flags).test(message));
